import { env } from '../env'

/**
 * The module registry.
 *
 * The AboveStore shell fetches this from its Portal API. This shell keeps it local and
 * configuration-driven instead, because Relay ships to customers who do not run that platform —
 * the reason this shell exists at its own copy rather than as a dependency on theirs.
 *
 * `relay` is built in and always present. The rest are federated remotes, each enabled only when
 * its URL is configured, so a Relay-only deployment carries no reference to modules its customer
 * does not own.
 */

export interface ModuleEntry {
  /** Route segment and federation scope name. */
  name: string
  /**
   * Where this module mounts.
   *
   * Relay is at the **root**, not at `/relay`, and that is deliberate: the Blazor client's URLs are
   * `/devices`, `/admin/users`, `/ota/rollouts`, and an operator's bookmarks and muscle memory are
   * part of what "port it, do not change it" protects. Federated remotes take a prefix each,
   * because they are guests.
   */
  basePath: string
  displayName: string
  /** A lucide-react icon name; the shell maps it to a component. */
  icon: string
  /** Permission required to see and open it. */
  permissionKey: string
  sortOrder: number
  /** Absent for the built-in module; a remoteEntry.js URL for a federated one. */
  remoteUrl?: string
}

const BUILT_IN: ModuleEntry = {
  name: 'relay',
  basePath: '/',
  displayName: 'Devices',
  icon: 'MonitorSmartphone',
  permissionKey: 'device.view',
  sortOrder: 10,
}

/**
 * Optional remotes from the Reporting project. These are the reason this shell can host more than
 * Relay: a customer running both products gets the reporting front end here rather than a second
 * portal, and one who does not never loads it.
 */
const OPTIONAL: Array<Omit<ModuleEntry, 'remoteUrl' | 'basePath'> & { urlKey: string }> = [
  {
    name: 'reporting',
    displayName: 'Reporting',
    icon: 'BarChart2',
    permissionKey: 'reports.view',
    sortOrder: 20,
    urlKey: 'REMOTE_REPORTING',
  },
  {
    name: 'dashboard',
    displayName: 'Dashboards',
    icon: 'LayoutDashboard',
    permissionKey: 'reports.view',
    sortOrder: 30,
    urlKey: 'REMOTE_DASHBOARD',
  },
]

/** Every module this deployment is configured for, in display order. */
export function availableModules(): ModuleEntry[] {
  const remotes = OPTIONAL.flatMap(({ urlKey, ...entry }) => {
    const remoteUrl = env(urlKey, undefined, '')
    return remoteUrl ? [{ ...entry, remoteUrl, basePath: `/${entry.name}` }] : []
  })
  return [BUILT_IN, ...remotes].sort((a, b) => a.sortOrder - b.sortOrder)
}
